import { createSimpleSelector } from '../selector-utils';
import { MakeRef } from '../types';
import { createActionsWithReducer } from '../utils';
import { createSingleEntityAdapter } from './adapter';
import {
  IsCustomActions,
  NotNull,
  SingleEntityState,
  SingleEntityStoreCustomActions,
  SingleEntityStoreOptions,
} from './types';

export function createSingleEntityStore<T extends object | null>() {
  return <U extends SingleEntityStoreOptions<T>>(namespace: string, options?: U) => {
    const ns = `entity/${namespace}`;

    const baseSelector = createSimpleSelector(
      (root: any) => <SingleEntityState<T>>root[ns],
      state => state,
    );

    const makeRef: MakeRef<NotNull<T>> = (mapFrom, mapTo, source) => ({ mapFrom, mapTo, source });
    const { adapter, initialState, selectors, defaultActions } = createSingleEntityAdapter<T>(
      baseSelector,
      ns,
      options?.references?.(makeRef) || [],
    );

    const mergedActions = <
      IsCustomActions<U['customActions']> extends true
        ? typeof defaultActions & SingleEntityStoreCustomActions<U>
        : typeof defaultActions
    >{
      ...(<typeof defaultActions>defaultActions),
      ...(<SingleEntityStoreCustomActions<U>>(options?.customActions?.(adapter) || {})),
    };

    const { actions, reducer } = createActionsWithReducer(namespace, initialState, mergedActions);
    return {
      namespace: ns,
      selectors,
      actions,
      reducer,
    };
  };
}
